import React from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import ViewSelector from '../ui/ViewSelector';
import { ListView } from '../../services/listViewService';

interface DataTableToolbarProps {
  title: string;
  count: number;
  filteredCount?: number;
  searchQuery: string;
  searchPlaceholder: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit?: () => void;
  views: ListView[];
  currentViewId: string | null;
  onSelectView: (viewId: string | number) => void;
  onSaveView: () => void;
  onSaveAsView: () => void;
  onDeleteView: (viewId: string) => void;
  onSetDefaultView: (viewId: string) => void;
  onManageColumns: () => void;
  isAdmin?: boolean;
  actions?: React.ReactNode;
  hideSearch?: boolean;
  hideCount?: boolean;
}

/**
 * Reusable toolbar component for tables with search, view management, and column management.
 * Provides a consistent UI across all table pages.
 */
const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  title,
  count,
  filteredCount,
  searchQuery,
  searchPlaceholder,
  onSearchChange,
  onSearchSubmit,
  views,
  currentViewId,
  onSelectView,
  onSaveView,
  onSaveAsView,
  onDeleteView,
  onSetDefaultView,
  onManageColumns,
  isAdmin = false,
  actions,
  hideSearch = false,
  hideCount = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      {/* Title Row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 1 }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
            {title} {!hideCount && `(${count}${filteredCount !== undefined && filteredCount !== count ? ` of ${count}` : ''})`}
          </h2>
        </div>

        {/* Actions & Controls */}
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
          flexWrap="wrap"
        >
          {actions && <Box sx={{ display: { xs: '100%', sm: 'auto' } }}>{actions}</Box>}

          <ViewSelector
            views={views}
            currentViewId={currentViewId}
            onSelectView={onSelectView}
            onSaveView={onSaveView}
            onSaveAsView={onSaveAsView}
            onDeleteView={onDeleteView}
            onSetDefault={onSetDefaultView}
            isAdmin={isAdmin}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={onManageColumns}
            sx={{
              whiteSpace: 'nowrap',
              textTransform: 'none',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              px: { xs: 1, md: 2 },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
              Manage Columns
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
              Columns
            </Box>
          </Button>
        </Box>
      </Box>

      {/* Search Row */}
      {!hideSearch && (
        <Box mb={3} display="flex" gap={1} alignItems="stretch">
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={onSearchSubmit ? (e) => { if (e.key === 'Enter') onSearchSubmit(); } : undefined}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {onSearchSubmit && (
            <Button
              variant="outlined"
              onClick={onSearchSubmit}
              sx={{ minWidth: 48, px: 1.5, flexShrink: 0 }}
              aria-label="Search"
            >
              <SearchIcon />
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DataTableToolbar;
